import React, { useEffect } from "react";
import classNames from "classnames";
import { Dropdown } from "react-bootstrap";
import moment from "moment";

// Componentes
import {
  TextMessage,
  ImageMessage,
  FileMessage,
  AudioMessage,
  VideoMessage,
  StickerMessage,
  ContactMessage,
  LocationMessage,
} from "./MessageFormat";
import {
  InitialsAgent,
  MessageStatus,
  formatMessageTime,
} from "./ChatComponents";
import ReplyingMessage from "./ReplyingMessage";

// Assets
import botChat from "../../assets/images/bot-chat.png";

// Constants
const baseURl = process.env.REACT_APP_API;

/**
 * Descarga la imagen
 *
 * @param {string} url - URL de la imagen
 * @returns {void} No retorna nada
 * @throws {Error} Error al descargar la imagen
 */
const handleDownloadImage = async (url) => {
  try {
    const path = `${baseURl}/${url}`;

    // Obtener la imagen
    const response = await fetch(path);
    const blob = await response.blob();

    // Crear una URL para el blob
    const blobUrl = window.URL.createObjectURL(blob);

    // Crear un enlace para descargar la imagen
    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", "image");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Liberar el objeto URL creado para el blob
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Error al descargar la imagen:", error);
  }
};

/**
 * Muestra las acciones del mensaje
 *
 * @param {object} props - Propiedades del componente
 * @param {function} props.onReply - Función para responder el mensaje
 * @param {object} props.message - Mensaje actual
 * @returns {JSX.Element} Componente de React
 */
const DropdownActions = ({ onReply, message }) => (
  <>
    <Dropdown.Toggle
      variant="link"
      className="btn btn-sm btn-link arrow-none shadow-none"
    >
      <i className="uil uil-ellipsis-v text-secondary" />
    </Dropdown.Toggle>

    <Dropdown.Menu>
      <Dropdown.Item onClick={onReply}>Responder Mensaje</Dropdown.Item>
      {message.type === "text" && (
        <Dropdown.Item
          onClick={() => {
            navigator.clipboard.writeText(message.object);
          }}
        >
          Copiar
        </Dropdown.Item>
      )}
      {message.type === "image" && (
        <Dropdown.Item
          onClick={() => {
            handleDownloadImage(message.object);
          }}
        >
          Descargar
        </Dropdown.Item>
      )}
    </Dropdown.Menu>
  </>
);

/**
 * Muestra el mensaje con el formato correspondiente
 *
 * @param {object} props - Propiedades del componente
 * @param {string} props.type - Tipo de mensaje
 * @param {object} props.message - Mensaje actual
 * @param {function} props.setShow - Función para mostrar el modal
 * @returns {JSX.Element} Componente de React
 */
const MessageFormatComponent = ({ type, message, setShow }) => {
  const messageComponents = {
    text: <TextMessage text={message.object} />,
    image: (
      <ImageMessage
        message={message}
        alt="chat-user"
        callBack={() => setShow(true)}
      />
    ),
    file: <FileMessage message={message} />,
    audio: <AudioMessage message={message} />,
    video: <VideoMessage message={message} callBack={() => setShow(true)} />,
    sticker: <StickerMessage message={message} />,
    contact: <ContactMessage contact={message.object} />,
    map: <LocationMessage location={message.object} />,
  };

  return messageComponents[type] || null;
};

/**
 * Componente para mostrar un mensaje en el chat
 *
 * @param {object} props - Propiedades del componente
 * @param {object} props.message - Mensaje
 * @param {function} props.onReply - Función para responder el mensaje
 * @param {function} props.openModal - Función para abrir el modal
 * @param {function} props.fetchAgentData - Función para obtener la información de un agente
 * @param {object} props.agentInfo - Información de los agentes
 * @returns {JSX.Element} Componente de React
 */
const Message = ({
  message,
  onReply,
  openModal,
  fetchAgentData,
  agentInfo,
}) => {
  // Obtiene el agente que envió el mensaje
  useEffect(() => {
    if (
      message.attended !== "bot" &&
      message.attended !== "client" &&
      !agentInfo[message.attended]
    ) {
      fetchAgentData(message.attended);
    }
  }, [message.attended, fetchAgentData, agentInfo]);

  // Abre el modal
  const handleOpenModal = () => openModal(message.id);

  return (
    <>
      <li
        className={classNames("clearfix mt-1", {
          odd: message.status === "send",
        })}
      >
        <div className="conversation-text">
          {/* Avatar o iniciales del agente */}
          {message.attended !== "client" && message.attended !== "agent" && (
            <div className="chat-avatar ms-2">
              {message.attended === "bot" ? (
                <img
                  src={botChat}
                  alt="chat-user"
                  className="rounded"
                  title="Agente Bot"
                />
              ) : agentInfo[message.attended] ? (
                <InitialsAgent agent={agentInfo[message.attended]} />
              ) : null}
            </div>
          )}

          <div className="ctext-wrap py-1">
            {/* Mensaje de respuesta, si existe */}
            {message.idRef && (
              <ReplyingMessage reply={message} setReply={null} />
            )}

            {/* Mensaje formateado */}
            <MessageFormatComponent
              type={message.type}
              message={message}
              setShow={handleOpenModal}
            />
            <div className="d-flex justify-content-between">
              {/* Hora del mensaje y el estado del mensaje */}
              <span className="d-flex justify-content-end align-items-center text-secondary gap-1 mt-2">
                {message.reaction}
              </span>
              <span className="d-flex justifyalign-content-start align-items-center text-secondary gap-1 mt-1">
                <span className="font-10">
                  {formatMessageTime(message.date)}
                </span>
                {(message.status === "send" ||
                  message.statesms === "deleted") && (
                  <MessageStatus status={message.statesms} />
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Acciones del mensaje */}
        <Dropdown className="conversation-actions">
          <DropdownActions onReply={() => onReply(message)} message={message} />
        </Dropdown>
      </li>
    </>
  );
};

export default Message;
